import React, { useState, useEffect } from 'react';
import './EventForm.css';
import ImageUpload from '../common/ImageUpload';

// Default contract address from environment
const DEFAULT_CONTRACT_ADDRESS = process.env.REACT_APP_EVENT_TICKET_ADDRESS ||
                                  process.env.REACT_APP_CONTRACT_ADDRESS || '';
const DEFAULT_CHAIN_ID = parseInt(process.env.REACT_APP_CHAIN_ID || '84532', 10);

// Event categories
const EVENT_CATEGORIES = [
  { value: '', label: 'Select Category' },
  { value: 'music', label: 'Music & Concerts' },
  { value: 'sports', label: 'Sports' },
  { value: 'theater', label: 'Theater & Performing Arts' },
  { value: 'conference', label: 'Conference & Summit' },
  { value: 'festival', label: 'Festival' },
  { value: 'lecture', label: 'Lecture & Seminar' },
  { value: 'workshop', label: 'Workshop & Training' },
  { value: 'networking', label: 'Networking & Social' },
  { value: 'exhibition', label: 'Exhibition & Art' },
  { value: 'comedy', label: 'Comedy & Entertainment' },
  { value: 'food', label: 'Food & Drink' },
  { value: 'charity', label: 'Charity & Fundraiser' },
  { value: 'gaming', label: 'Gaming & Esports' },
  { value: 'crypto', label: 'Crypto & Web3' },
  { value: 'other', label: 'Other' },
];

// Link expiration options for private events (hours after event start)
const LINK_EXPIRATION_OPTIONS = [
  { value: 0, label: 'Never expire' },
  { value: 1, label: '1 hour after event start' },
  { value: 2, label: '2 hours after event start' },
  { value: 6, label: '6 hours after event start' },
  { value: 12, label: '12 hours after event start (default)' },
  { value: 24, label: '24 hours after event start' },
  { value: 48, label: '48 hours after event start' },
];

// Geocoding API endpoint (using OpenStreetMap Nominatim - free, no API key needed)
const GEOCODING_API = 'https://nominatim.openstreetmap.org/search';

const EventForm = ({ event, onSave, onCancel, saveStatus }) => {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    category: '',
    date: '',
    time: '',
    endDate: '', // End date for multi-day events
    endTime: '', // End time
    image: '',
    imageMetadata: null,
    contractAddress: DEFAULT_CONTRACT_ADDRESS,
    chainId: DEFAULT_CHAIN_ID,
    description: '',
    active: true,
    visibility: 'public', // 'public' or 'private'
    linkExpiresAfter: 12, // hours after event start (for private events)
    venue: '',
    venueCoordinates: null, // { lat, lng, displayName }
    capacity: '',
    ticketTypes: [],
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [fetchingCoordinates, setFetchingCoordinates] = useState(false);
  const [coordinatesError, setCoordinatesError] = useState('');

  useEffect(() => {
    if (event) {
      const eventDate = new Date(event.date);
      const pad2 = (n) => String(n).padStart(2, '0');
      const timeStr = `${pad2(eventDate.getHours())}:${pad2(eventDate.getMinutes())}`;
      // End time from event or default to start + 4h
      let endDateStr = '';
      let endTimeStr = '';
      if (event.endTime) {
        const endDt = new Date(event.endTime * 1000);
        endDateStr = endDt.toISOString().split('T')[0];
        endTimeStr = `${pad2(endDt.getHours())}:${pad2(endDt.getMinutes())}`;
      }
      setFormData({
        ...event,
        date: eventDate.toISOString().split('T')[0],
        time: timeStr,
        endDate: endDateStr,
        endTime: endTimeStr,
        imageMetadata: event.imageMetadata || null,
        category: event.category || '',
        visibility: event.visibility || 'public',
        linkExpiresAfter: event.linkExpiresAfter ?? 12,
        venue: event.venue || '',
        venueCoordinates: event.venueCoordinates || null,
        capacity: event.capacity || '',
        ticketTypes: event.ticketTypes || [],
      });
    }
  }, [event]);

  // Auto-fetch coordinates when venue changes
  const fetchVenueCoordinates = async (venueQuery, retries = 2) => {
    if (!venueQuery || venueQuery.trim().length < 3) {
      return;
    }

    setFetchingCoordinates(true);
    setCoordinatesError('');

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        if (attempt > 0) {
          await new Promise(r => setTimeout(r, 1500 * attempt));
        }
        const response = await fetch(
          `${GEOCODING_API}?q=${encodeURIComponent(venueQuery)}&format=json&limit=1`
        );

        if (response.status === 425 || response.status === 429) {
          if (attempt < retries) continue;
          setCoordinatesError('Geocoding rate limited. Try again in a moment.');
          setFetchingCoordinates(false);
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data && data.length > 0) {
          const result = data[0];
          setFormData(prev => ({
            ...prev,
            venueCoordinates: {
              lat: parseFloat(result.lat),
              lng: parseFloat(result.lon),
              displayName: result.display_name,
            },
          }));
          setCoordinatesError('');
        } else {
          setCoordinatesError('Location not found. Try a more specific address.');
          setFormData(prev => ({
            ...prev,
            venueCoordinates: null,
          }));
        }
        setFetchingCoordinates(false);
        return;
      } catch (error) {
        if (attempt === retries) {
          console.error('Error fetching coordinates:', error);
          setCoordinatesError('Failed to fetch location. Please try again.');
        }
      }
    }
    setFetchingCoordinates(false);
  };

  // Debounced venue coordinate fetch
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.venue && formData.venue.trim().length >= 3) {
        fetchVenueCoordinates(formData.venue);
      }
    }, 1500); // Wait 1.5s after user stops typing (Nominatim rate limit)

    return () => clearTimeout(timeoutId);
  }, [formData.venue]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Event name is required';
    }

    if (!formData.date) {
      newErrors.date = 'Event date is required';
    }

    if (!formData.time) {
      newErrors.time = 'Event time is required';
    }

    // Validate event is in the future (only for new events)
    if (formData.date && formData.time && !event) {
      const eventDateTime = new Date(`${formData.date}T${formData.time}`);
      if (eventDateTime < new Date()) {
        newErrors.date = 'Event date must be in the future';
      }
    }

    // Validate end time is after start time
    if (formData.endDate && formData.endTime && formData.date && formData.time) {
      const start = new Date(`${formData.date}T${formData.time}`);
      const end = new Date(`${formData.endDate}T${formData.endTime}`);
      if (end <= start) {
        newErrors.endTime = 'End time must be after start time';
      }
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Event description is required';
    }

    const addr = (formData.contractAddress || '').trim();
    if (!addr || !addr.match(/^0x[a-fA-F0-9]{40}$/)) {
      newErrors.contractAddress = 'Valid contract address is required (0x...)';
    }

    if (!formData.chainId) {
      newErrors.chainId = 'Chain ID is required';
    }

    if (!formData.image) {
      newErrors.image = 'Event image is required. Upload an image to IPFS before creating the event.';
    } else if (!formData.image.startsWith('https://') && !formData.image.startsWith('ipfs://')) {
      newErrors.image = 'Image must be uploaded to IPFS or be a valid HTTPS URL';
    }

    // Validate ticket types
    if (formData.ticketTypes && formData.ticketTypes.length > 0) {
      for (let i = 0; i < formData.ticketTypes.length; i++) {
        const tt = formData.ticketTypes[i];
        if (!tt.name?.trim()) {
          newErrors[`ticketType_${i}_name`] = `Ticket type #${i + 1}: name is required`;
        }
        if (!tt.price || parseFloat(tt.price) <= 0 || isNaN(parseFloat(tt.price))) {
          newErrors[`ticketType_${i}_price`] = `Ticket type #${i + 1}: price must be > 0`;
        } else {
          const decimals = (String(tt.price).split('.')[1] || '').length;
          if (decimals > 2) {
            newErrors[`ticketType_${i}_price`] = `Ticket type #${i + 1}: PGT supports max 2 decimals`;
          }
        }
        if (!tt.maxSupply || parseInt(tt.maxSupply) <= 0 || isNaN(parseInt(tt.maxSupply))) {
          newErrors[`ticketType_${i}_maxSupply`] = `Ticket type #${i + 1}: supply must be > 0`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      // Combine date and time
      const eventDateTime = new Date(`${formData.date}T${formData.time}`).toISOString();

      // Compute endTime if user provided it
      let endTimeISO = null;
      if (formData.endDate && formData.endTime) {
        endTimeISO = new Date(`${formData.endDate}T${formData.endTime}`).toISOString();
      }

      const eventData = {
        ...formData,
        date: eventDateTime,
        endTime: endTimeISO, // null → EventManagement defaults to +4h
        id: formData.id || `event-${Date.now()}`,
      };

      // Remove form-only fields not needed by parent
      delete eventData.time;
      delete eventData.endDate;

      await onSave(eventData);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to save event. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addTicketType = () => {
    setFormData(prev => ({
      ...prev,
      ticketTypes: [
        ...prev.ticketTypes,
        {
          id: Date.now(),
          name: '',
          price: '',
          maxSupply: '',
          description: '',
        },
      ],
    }));
  };

  const removeTicketType = (index) => {
    setFormData(prev => ({
      ...prev,
      ticketTypes: prev.ticketTypes.filter((_, i) => i !== index),
    }));
  };

  const updateTicketType = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      ticketTypes: prev.ticketTypes.map((ticket, i) =>
        i === index ? { ...ticket, [field]: value } : ticket
      ),
    }));
  };

  return (
    <div className="event-form-container">
      <div className="event-form-header">
        <button className="btn-back" onClick={onCancel}>
          ← Back
        </button>
        <div>
          <h1>{event ? 'Edit Event' : 'Create New Event'}</h1>
          <p>{event ? 'Update event details and settings' : 'Fill in the details to create a new event'}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="event-form">
        <div className="form-section">
          <h2>Basic Information</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">
                Event Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Pregame Launch Party"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
              >
                {EVENT_CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="venue">Venue</label>
              <div className="venue-input-wrapper">
                <input
                  type="text"
                  id="venue"
                  name="venue"
                  value={formData.venue}
                  onChange={handleChange}
                  placeholder="e.g., Madison Square Garden, New York"
                />
                <button
                  type="button"
                  className="btn-lookup-venue"
                  onClick={() => fetchVenueCoordinates(formData.venue)}
                  disabled={fetchingCoordinates || !formData.venue}
                  title="Lookup venue coordinates"
                >
                  {fetchingCoordinates ? '...' : '📍'}
                </button>
              </div>
              {fetchingCoordinates && (
                <span className="venue-status loading">Looking up location...</span>
              )}
              {coordinatesError && (
                <span className="venue-status error">{coordinatesError}</span>
              )}
              {formData.venueCoordinates && !fetchingCoordinates && (
                <div className="venue-coordinates-info">
                  <span className="coordinates-found">✓ Location found</span>
                  <span className="coordinates-display">
                    {formData.venueCoordinates.lat.toFixed(4)}, {formData.venueCoordinates.lng.toFixed(4)}
                  </span>
                  <a
                    href={`https://www.google.com/maps?q=${formData.venueCoordinates.lat},${formData.venueCoordinates.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="preview-map-link"
                  >
                    Preview on map ↗
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date">
                Event Date <span className="required">*</span>
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className={errors.date ? 'error' : ''}
              />
              {errors.date && <span className="error-message">{errors.date}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="time">
                Event Time <span className="required">*</span>
              </label>
              <input
                type="time"
                id="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                className={errors.time ? 'error' : ''}
              />
              {errors.time && <span className="error-message">{errors.time}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="capacity">Capacity</label>
              <input
                type="number"
                id="capacity"
                name="capacity"
                value={formData.capacity}
                onChange={handleChange}
                placeholder="e.g., 1000"
                min="0"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="endDate">End Date</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                min={formData.date || undefined}
                className={errors.endTime ? 'error' : ''}
              />
              <small className="field-hint">Leave blank for default 4h duration</small>
            </div>
            <div className="form-group">
              <label htmlFor="endTime">End Time</label>
              <input
                type="time"
                id="endTime"
                name="endTime"
                value={formData.endTime}
                onChange={handleChange}
                className={errors.endTime ? 'error' : ''}
              />
              {errors.endTime && <span className="error-message">{errors.endTime}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">
              Description <span className="required">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe your event..."
              rows="4"
              className={errors.description ? 'error' : ''}
            />
            {errors.description && <span className="error-message">{errors.description}</span>}
          </div>

          <ImageUpload
            label="Event Image"
            value={formData.image}
            onChange={(url, metadata) => {
              setFormData(prev => ({
                ...prev,
                image: url,
                imageMetadata: metadata || null,
              }));
              if (errors.image) {
                setErrors(prev => ({ ...prev, image: '' }));
              }
            }}
            required
            helpText="Required: Upload event image to IPFS before creating. Each ticket is an NFT — the image is stored permanently on-chain."
            maxSize={10 * 1024 * 1024}
            allowedTypes={['image/jpeg', 'image/png', 'image/gif', 'image/webp']}
          />
          {errors.image && <span className="error-message">{errors.image}</span>}
        </div>

        <div className="form-section">
          <h2>Blockchain Configuration</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="contractAddress">
                Contract Address <span className="required">*</span>
              </label>
              <input
                type="text"
                id="contractAddress"
                name="contractAddress"
                value={formData.contractAddress}
                onChange={handleChange}
                placeholder="0x..."
                className={errors.contractAddress ? 'error' : ''}
              />
              {errors.contractAddress && (
                <span className="error-message">{errors.contractAddress}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="chainId">
                Chain ID <span className="required">*</span>
              </label>
              <select
                id="chainId"
                name="chainId"
                value={formData.chainId}
                onChange={handleChange}
                className={errors.chainId ? 'error' : ''}
              >
                <option value="84532">Base Sepolia (84532)</option>
                <option value="8453">Base Mainnet (8453)</option>
                <option value="11155111">Sepolia (11155111)</option>
                <option value="1">Ethereum Mainnet (1)</option>
              </select>
              {errors.chainId && <span className="error-message">{errors.chainId}</span>}
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header">
            <h2>Ticket Types</h2>
            <button type="button" className="btn-add-ticket" onClick={addTicketType}>
              + Add Ticket Type
            </button>
          </div>

          {formData.ticketTypes.length === 0 ? (
            <div className="no-tickets">
              <p>No ticket types added yet. Click "Add Ticket Type" to create one.</p>
            </div>
          ) : (
            <div className="ticket-types-list">
              {formData.ticketTypes.map((ticket, index) => (
                <div key={ticket.id} className="ticket-type-card">
                  <div className="ticket-type-header">
                    <h3>Ticket Type {index + 1}</h3>
                    <button
                      type="button"
                      className="btn-remove-ticket"
                      onClick={() => removeTicketType(index)}
                    >
                      Remove
                    </button>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Ticket Name <span className="required">*</span></label>
                      <input
                        type="text"
                        value={ticket.name}
                        onChange={(e) => updateTicketType(index, 'name', e.target.value)}
                        placeholder="e.g., VIP, General Admission"
                        className={errors[`ticketType_${index}_name`] ? 'error' : ''}
                      />
                      {errors[`ticketType_${index}_name`] && <span className="error-message">{errors[`ticketType_${index}_name`]}</span>}
                    </div>

                    <div className="form-group">
                      <label>Price (PGT) <span className="required">*</span></label>
                      <input
                        type="number"
                        value={ticket.price}
                        onChange={(e) => updateTicketType(index, 'price', e.target.value)}
                        placeholder="e.g., 50.00"
                        min="0.01"
                        step="0.01"
                        className={errors[`ticketType_${index}_price`] ? 'error' : ''}
                      />
                      {errors[`ticketType_${index}_price`] && <span className="error-message">{errors[`ticketType_${index}_price`]}</span>}
                    </div>

                    <div className="form-group">
                      <label>Max Supply <span className="required">*</span></label>
                      <input
                        type="number"
                        value={ticket.maxSupply}
                        onChange={(e) => updateTicketType(index, 'maxSupply', e.target.value)}
                        placeholder="e.g., 100"
                        min="1"
                        className={errors[`ticketType_${index}_maxSupply`] ? 'error' : ''}
                      />
                      {errors[`ticketType_${index}_maxSupply`] && <span className="error-message">{errors[`ticketType_${index}_maxSupply`]}</span>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <input
                      type="text"
                      value={ticket.description}
                      onChange={(e) => updateTicketType(index, 'description', e.target.value)}
                      placeholder="Brief description of this ticket type"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-section">
          <h2>Status & Visibility</h2>

          <div className="form-group">
            <label>Event Visibility</label>
            <div className="visibility-options">
              <label className="radio-label">
                <input
                  type="radio"
                  name="visibility"
                  value="public"
                  checked={formData.visibility === 'public'}
                  onChange={handleChange}
                />
                <span className="radio-text">
                  <strong>Public</strong>
                  <small>Listed in event directory, anyone can find and purchase tickets</small>
                </span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="visibility"
                  value="private"
                  checked={formData.visibility === 'private'}
                  onChange={handleChange}
                />
                <span className="radio-text">
                  <strong>Private</strong>
                  <small>Not listed publicly, only accessible via direct event link</small>
                </span>
              </label>
            </div>
          </div>

          {/* Link Expiration - only for private events */}
          {formData.visibility === 'private' && (
            <div className="form-group private-event-options">
              <label htmlFor="linkExpiresAfter">
                Link Expiration
                <span className="label-hint">(when the event link becomes unavailable)</span>
              </label>
              <select
                id="linkExpiresAfter"
                name="linkExpiresAfter"
                value={formData.linkExpiresAfter}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  linkExpiresAfter: parseInt(e.target.value, 10),
                }))}
              >
                {LINK_EXPIRATION_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="helper-text">
                After this time, the event link will no longer work for regular users.
                Admins and event managers can still access the event.
              </p>
            </div>
          )}

          <div className="form-group-checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
              />
              <span>Event is active and visible to users</span>
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn-save" disabled={saving || !formData.image}>
            {saving ? 'Saving...' : event ? 'Update Event' : 'Create Event'}
          </button>
          {!formData.image && !saving && (
            <p className="save-status" style={{ color: '#e74c3c' }}>Upload an event image to IPFS before creating</p>
          )}
          {saving && saveStatus && (
            <p className="save-status">{saveStatus}</p>
          )}
        </div>
      </form>
    </div>
  );
};

export default EventForm;
